import {
  Component,
  forwardRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';
import { ApiService } from 'src/app/services/api.service';

@Component({
  selector: 'app-org-type-selector',
  templateUrl: './org-type-selector.component.html',
  styleUrls: ['./org-type-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OrgTypeSelectorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => OrgTypeSelectorComponent),
      multi: true,
    },
  ],
})
export class OrgTypeSelectorComponent
  implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
  @Input() label: string = 'Organisation Type';
  @Input() placeholder: string = 'Enter organisation type';
  @Input() hintText: string = '';

  inputCtrl = new FormControl<any>('');
  filteredOrgTypes$: Observable<any[]> = of([]);
  organisationTypes: any[] = [];

  /** Resolved lazily in ngOnInit to avoid circular DI with NG_VALIDATORS. */
  private _ngControl: NgControl | null = null;

  /** Drives mat-form-field error visibility from the parent form control state. */
  readonly errorStateMatcher: ErrorStateMatcher = {
    isErrorState: () =>
      !!(this._ngControl?.control?.invalid && this._ngControl?.control?.touched),
  };

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidationChange: () => void = () => {};
  private destroy$ = new Subject<void>();

  constructor(
    private apiService: ApiService,
    private injector: Injector,
  ) {}

  ngOnInit(): void {
    // Injecting NgControl lazily (not via constructor) avoids the NG0200 circular DI
    // error caused by: OrgTypeSelectorComponent → NgControl → NG_VALIDATORS → OrgTypeSelectorComponent.
    this._ngControl = this.injector.get(NgControl, null);

    this.apiService.getOrganisations().subscribe((res: any) => {
      const types: any[] = res?.organisationtypes || [];
      this.organisationTypes = types.slice().sort((a: any, b: any) => {
        if (a.key === 'other') return 1;
        if (b.key === 'other') return -1;
        return a.name.localeCompare(b.name);
      });
      this.setupFilter();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupFilter(): void {
    this.filteredOrgTypes$ = this.inputCtrl.valueChanges.pipe(
      startWith(this.inputCtrl.value),
      debounceTime(200),
      map((val: any) => {
        const s = (
          typeof val === 'string' ? val : (val?.name ?? '')
        ).toLowerCase();
        const list = this.organisationTypes.filter((t: any) => t.key !== 'other');
        return s ? list.filter((t: any) => t.name.toLowerCase().includes(s)) : list;
      }),
      takeUntil(this.destroy$),
    );
  }

  // ---- ControlValueAccessor ----

  writeValue(val: any): void {
    this.inputCtrl.setValue(val ?? '', { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.inputCtrl.disable() : this.inputCtrl.enable();
  }

  // ---- Validator ----

  validate(control: AbstractControl): ValidationErrors | null {
    const val = control.value;
    if (!val) return null;
    const text =
      typeof val === 'string' ? val.trim() : (val.otherText ?? val.name ?? '');
    const isPredefined = this.organisationTypes.some(
      (t: any) => t.name.toLowerCase() === text.toLowerCase(),
    );
    if (!isPredefined && text.length > 15) {
      return { maxlength: true };
    }
    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidationChange = fn;
  }

  // ---- Accessors for template ----

  get parentControl(): AbstractControl | null {
    return this._ngControl?.control ?? null;
  }

  get isCustomOrgType(): boolean {
    const val = this.inputCtrl.value;
    if (!val) return false;
    if (typeof val === 'object') return val.key === 'other';
    const typed = (val as string).trim().toLowerCase();
    if (!typed) return false;
    return !this.organisationTypes.some(
      (t: any) => t.name.toLowerCase() === typed,
    );
  }

  // ---- Template event handlers ----

  displayOrgType(obj: any): string {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    return obj.name ?? obj.otherText ?? '';
  }

  getOrgTypeTooltip(name: string): string {
    const tooltips: Record<string, string> = {
      'Satellite Operator':
        'Choose this if you are a satellite operator wanting to be a user on the platform.',
      'Application Provider':
        'Choose this if you are a service provider. If an end user, choose Commercial/Enterprise.',
      Other: 'The type entered will be used in the KYC verification process.',
    };
    return tooltips[name] ?? '';
  }

  onInput(): void {
    const val = this.inputCtrl.value;
    const text = typeof val === 'string' ? val : '';
    // Emit raw string so the parent control re-validates on every keystroke
    this.onChange(text);
    // Mirror original behaviour: mark touched immediately when maxlength is exceeded
    if (text.trim().length > 15) {
      this.onTouched();
    }
  }

  onOptionSelected(selected: any): void {
    this.inputCtrl.setValue(selected, { emitEvent: false });
    this.onChange(selected);
    this.onValidationChange();
  }

  onBlur(): void {
    this.syncFromInput();
    this.onTouched();
  }

  Space(event: KeyboardEvent): void {
    const el = event.target as HTMLInputElement;
    if (el.selectionStart === 0 && event.code === 'Space') {
      event.preventDefault();
    }
  }

  private syncFromInput(): void {
    const val = this.inputCtrl.value;
    if (!val) {
      this.inputCtrl.setValue(null, { emitEvent: false });
      this.onChange(null);
      return;
    }
    if (typeof val === 'object') {
      this.onChange(val);
      return;
    }
    const typed = (val as string).trim();
    if (!typed) {
      this.inputCtrl.setValue(null, { emitEvent: false });
      this.onChange(null);
      return;
    }
    const match = this.organisationTypes.find(
      (t: any) => t.name.toLowerCase() === typed.toLowerCase(),
    );
    if (match) {
      this.inputCtrl.setValue(match, { emitEvent: false });
      this.onChange(match);
    } else {
      const obj = { key: 'other', name: typed, otherText: typed };
      this.inputCtrl.setValue(obj, { emitEvent: false });
      this.onChange(obj);
    }
    this.onValidationChange();
  }
}
